import React from 'react';
import styled from 'styled-components';
import type { Conversation } from '../../services/api';
import { contentColumnPadding } from '../../theme/constants';

interface Props {
  conversations: Conversation[];
  activeConversationId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDelete: (id: number) => void;
  isOpen: boolean;
  onClose: () => void;
}

const ConversationSidebar: React.FC<Props> = ({
  conversations,
  activeConversationId,
  onSelect,
  onNew,
  onDelete,
  isOpen,
  onClose,
}) => {
  return (
    <>
      {isOpen && <Overlay onClick={onClose} />}
      <SidebarPanel $open={isOpen}>
        <SidebarHeader>
          <SidebarTitle>Conversations</SidebarTitle>
          <CloseBtn onClick={onClose} aria-label="Close sidebar">✕</CloseBtn>
        </SidebarHeader>

        <NewChatBtn onClick={onNew}>
          <span>+</span> New conversation
        </NewChatBtn>

        <ConversationList>
          {conversations.length === 0 && (
            <EmptyState>No conversations yet. Ask your first question!</EmptyState>
          )}
          {conversations.map((c) => (
            <ConversationItem
              key={c.id}
              $active={c.id === activeConversationId}
              onClick={() => onSelect(c.id)}
            >
              <ConvTitle>{c.title || 'New conversation'}</ConvTitle>
              <ConvMeta>{formatDate(c.updated_at ?? c.created_at)}</ConvMeta>
              <DeleteBtn
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(c.id);
                }}
                aria-label="Delete conversation"
              >
                ✕
              </DeleteBtn>
            </ConversationItem>
          ))}
        </ConversationList>
      </SidebarPanel>
    </>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default ConversationSidebar;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.av2.overlay};
  z-index: 400;

  @media (min-width: 769px) {
    display: none;
  }
`;

const SidebarPanel = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 60px;
  left: 0;
  bottom: 0;
  width: 280px;
  background: ${({ theme }) => theme.av2.sidebarBg};
  border-right: 1px solid ${({ theme }) => theme.av2.border};
  display: flex;
  flex-direction: column;
  z-index: 401;
  transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  transition: transform 0.25s ease;

  @media (min-width: 769px) {
    position: sticky;
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
    width: ${({ $open }) => ($open ? '280px' : '0')};
    min-width: ${({ $open }) => ($open ? '280px' : '0')};
    overflow: hidden;
    transition: width 0.25s ease, min-width 0.25s ease, transform 0.25s ease;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.av2.border};
`;

const SidebarTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.av2.textPrimary};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.av2.textMuted};
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.av2.hoverBg};
  }
`;

const NewChatBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 24px);
  margin: 12px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px dashed ${({ theme }) => theme.av2.primary};
  background: ${({ theme }) => theme.av2.primaryTint};
  color: ${({ theme }) => theme.av2.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.primaryTintStrong};
  }

  span {
    font-size: 18px;
    line-height: 1;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const ConversationItem = styled.div<{ $active: boolean }>`
  position: relative;
  padding: 10px 16px 10px 14px;
  cursor: pointer;
  border-left: 3px solid ${({ theme, $active }) => ($active ? theme.av2.primary : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.av2.primaryTint : 'transparent')};
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.av2.primaryTintStrong : theme.av2.hoverBg};

    button {
      opacity: 1;
    }
  }
`;

const ConvTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.av2.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
`;

const ConvMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.av2.textMuted};
  margin-top: 2px;
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 13px;
  cursor: pointer;
  opacity: 0;
  padding: 3px 6px;
  border-radius: 4px;
  transition: opacity 0.12s ease, background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.errorBg};
    color: ${({ theme }) => theme.av2.errorBorder};
  }
`;

const EmptyState = styled.p`
  padding: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.av2.textMuted};
  text-align: center;
`;
